import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';

interface KPICardProps {
  title: string;
  value: number | string;
  change: number;
  sparklineData?: Array<{ value: number }>;
  format?: 'number' | 'currency' | 'percentage';
  icon?: React.ReactNode;
  color?: string;
  subtitle?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  change,
  sparklineData,
  format = 'number',
  icon,
  color = '#1976d2',
  subtitle
}) => {
  const formatValue = (val: number | string) => {
    if (typeof val === 'string') return val;
    
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(val);
      case 'percentage':
        return `${val.toFixed(1)}%`;
      default:
        return new Intl.NumberFormat('en-US', {
          notation: 'compact',
          maximumFractionDigits: 1
        }).format(val);
    }
  };

  const isPositive = change >= 0;

  return (
    <Card 
      sx={{ 
        height: '100%', 
        position: 'relative',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography color="textSecondary" variant="subtitle2" fontWeight={500}>
            {title}
          </Typography>
          <Box sx={{ color }}>
            {icon}
          </Box>
        </Box>
        
        <Typography variant="h4" component="div" gutterBottom fontWeight={600}>
          {formatValue(value)}
        </Typography>
        
        <Box display="flex" alignItems="center" gap={1}>
          {isPositive ? (
            <TrendingUp color="success" fontSize="small" />
          ) : (
            <TrendingDown color="error" fontSize="small" />
          )}
          <Typography
            variant="body2"
            color={isPositive ? 'success.main' : 'error.main'}
            fontWeight={500}
          >
            {isPositive ? '+' : ''}{change.toFixed(1)}%
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {subtitle || 'vs last period'}
          </Typography>
        </Box>
        
        {sparklineData && sparklineData.length > 0 && (
          <Box mt={2} height={40}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparklineData}>
                <Tooltip 
                  contentStyle={{ 
                    display: 'none' 
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={color}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default KPICard;